import { type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';

import styles from './PopoutTree.module.css';

interface FloatingTreePanelProps {
  anchorRowRect: DOMRect;
  popoutWidth: number;
  children: ReactNode;
  onClick: () => void;
}

const VIEWPORT_PADDING = 8;
/** Matches the padding on .floatingTree so the first row aligns with the drilled node */
const PANEL_PADDING = 12;

const findPreviousPanel = (el: HTMLElement): HTMLElement | null => {
  let prev = el.previousElementSibling;
  while (prev) {
    if (
      prev instanceof HTMLElement &&
      prev.hasAttribute('data-floating-panel')
    ) {
      return prev;
    }
    prev = prev.previousElementSibling;
  }
  return null;
};

const computeLeft = (
  anchorRowRect: DOMRect,
  containerRect: DOMRect,
  popoutWidth: number,
  floating: HTMLElement
): number => {
  const fitsOnRight =
    anchorRowRect.right + popoutWidth <= window.innerWidth - VIEWPORT_PADDING;

  if (fitsOnRight) {
    return anchorRowRect.right - containerRect.left;
  }

  const baseLeft = anchorRowRect.left - containerRect.left - popoutWidth;

  const previousPanel = findPreviousPanel(floating);
  if (!previousPanel) return baseLeft;

  const previousPanelRect = previousPanel.getBoundingClientRect();
  const floatingLeft = containerRect.left + baseLeft;
  const floatingRight = floatingLeft + popoutWidth;
  const overlapsHorizontally =
    floatingLeft < previousPanelRect.right &&
    floatingRight > previousPanelRect.left;

  if (overlapsHorizontally) {
    return previousPanelRect.left - containerRect.left - popoutWidth;
  }

  return baseLeft;
};

const shiftToFitViewport = (floating: HTMLElement) => {
  const rect = floating.getBoundingClientRect();
  const viewportBottom = window.innerHeight - VIEWPORT_PADDING;

  if (rect.bottom <= viewportBottom) return;

  const currentTop = parseFloat(floating.style.top) || 0;
  const overflow = rect.bottom - viewportBottom;
  floating.style.top = `${currentTop - overflow}px`;
};

export const FloatingTreePanel = ({
  anchorRowRect,
  popoutWidth,
  children,
  onClick,
}: FloatingTreePanelProps) => {
  const floatingRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const floating = floatingRef.current;
    if (!floating) return;

    const container = floating.offsetParent;
    if (!(container instanceof HTMLElement)) return;

    const containerRect = container.getBoundingClientRect();

    const left = computeLeft(
      anchorRowRect,
      containerRect,
      popoutWidth,
      floating
    );
    const top = anchorRowRect.top - containerRect.top - PANEL_PADDING;

    Object.assign(floating.style, {
      left: `${left}px`,
      top: `${top}px`,
    });
  }, [anchorRowRect, popoutWidth]);

  useEffect(() => {
    const floating = floatingRef.current;
    if (!floating) return;

    const ro = new ResizeObserver(() => shiftToFitViewport(floating));
    ro.observe(floating);

    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={floatingRef}
      className={styles.floatingTree}
      style={{ width: `${popoutWidth}px` }}
      onClick={onClick}
      data-testid="popout-tree-child"
      data-floating-panel
    >
      {children}
    </div>
  );
};
